import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  ChangeDetectorRef,
} from "@angular/core";
import { DatePipe } from "@angular/common";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormControl,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatOptionModule } from "@angular/material/core";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatTimepickerModule } from "@angular/material/timepicker";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { CommonModule } from "@angular/common";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectChange } from "@angular/material/select";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";

import { CourtsService } from "app/modules/courts/courts.service";
import { VenueService } from "app/shared/services/venue.service";
import { BookingService } from "../booking.service";
import { HttpErrorResponse } from "@angular/common/http";
import { formatVenueWallClockInput } from "app/shared/venue-wall-clock";
import { CUSTOMER_BOOKING_DATE_FORMATS } from "app/shared/customer-booking-date-formats";
import { AuthService } from "app/core/auth/auth.service";
import { VenueBookingMatchSettingsComponent } from "../matchmaking/venue-booking-match-settings.component";
import { VenueMatchmakingTriggerComponent } from "../matchmaking/venue-matchmaking-trigger.component";
import { VenuePlayerPickerComponent } from "./venue-player-picker.component";
import {
  buildBookingParticipantDisplay,
  playerIdentity,
} from "./booking-participant-display";
import { previewCourtPrice } from "./court-pricing-preview";
import { getSportDisplayName } from "app/shared/sport-display";
import { formatAedCurrency } from "app/shared/aed-currency";
import { formatProductDate } from "app/shared/product-date";
import {
  applyBookingSlot,
  bookingAvailabilitySlotKey,
  BookingAvailabilitySlot,
  formatAvailabilityDate,
  formatAvailabilityTime,
  getAvailableBookingDurations,
  normalizeBookingAvailability,
} from "./booking-availability-slots";

@Component({
  selector: "app-booking",
  templateUrl: "./booking.component.html",
  styleUrl: "./booking.component.scss",
  imports: [
    CommonModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatOptionModule,
    MatDividerModule,
    MatIconModule,
    MatRadioModule,
    MatSelectModule,
    MatTimepickerModule,
    MatButtonToggleModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    VenueBookingMatchSettingsComponent,
    VenueMatchmakingTriggerComponent,
    VenuePlayerPickerComponent,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: CUSTOMER_BOOKING_DATE_FORMATS },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingComponent implements OnInit {
  readonly formatAedCurrency = formatAedCurrency;
  readonly getSportDisplayName = getSportDisplayName;
  readonly dialogRef = inject(MatDialogRef<BookingComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  bookingForm: UntypedFormGroup;
  manualPlayerForm: UntypedFormGroup;
  isLoading: boolean = false;
  totalPrice = 0;
  effectivePricePerHour = 0;

  venues: any[] = [];
  courts: any[] = [];
  selectedCourt: any;
  duration: string = "30";
  availabilitySlots: BookingAvailabilitySlot[] = [];
  availabilityError: string | null = null;
  isLoadingAvailability = false;
  selectedAvailabilityStart: string | null = null;
  private availabilityRequestId = 0;
  globalError: string | null = null;
  today: Date = new Date();
  allPlayers: any[] = [];
  selectedPlayers: any[] = [];
  organizerPlayerId: string | null = null;
  isLoadingPlayers: boolean = false;
  isInviting: boolean = false;
  isCreatingPlayer: boolean = false;
  acceptedPlayers: any[] = [];
  pendingPlayers: any[] = [];
  showExistingPlayers: boolean = true;
  showNewPlayerForm: boolean = false;

  // Sports data properties
  sportsData: any = null;

  // Autocomplete search properties
  playerSearchControl = new FormControl("");
  filteredPlayers: any[] = [];
  searchTimeout: any;

  // Maintenance mode properties
  isMaintenanceMode: boolean = false;

  // Court operating hours
  courtOperatingHours: any = null;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private courtService: CourtsService,
    private bookingService: BookingService,
    private venueService: VenueService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
  ) {
    this.totalPrice = this.data?.booking?.totalPrice || 0;
    this.fetchVenues();
  }

  ngOnInit(): void {
    let start: Date;
    let end: Date;
    let diffMins = "30";

    try {
      start = this.data?.booking?.start
        ? new Date(this.data?.booking?.start)
        : new Date();
      end = this.data?.booking?.end
        ? new Date(this.data?.booking?.end)
        : new Date();

      if (isNaN(start.getTime())) start = new Date();
      if (isNaN(end.getTime())) end = new Date();

      const diffMs = end.getTime() - start.getTime();
      diffMins = Math.max(30, Math.round(diffMs / (1000 * 60))).toString();
    } catch {
      start = new Date();
      end = new Date();
    }

    this.bookingForm = this._formBuilder.group({
      id: this.data?.booking?.id || this.data?.booking?._id || "",
      venueId: [this.data?.booking?.venueId, [Validators.required]],
      courtId: [this.data?.booking?.courtId, [Validators.required]],
      userId: [],
      sports: ["soccer"],
      start: [start, [Validators.required]],
      duration: [diffMins, [Validators.required]],
      end: [end, [Validators.required]],
      paymentType: ["consolidated"],
      status: this.data?.booking?.status,
      notes: this.data?.booking?.notes,
      bookingType: [this.data?.booking?.bookingType || "private"],
      slots: [this.data?.booking?.slots ?? null],
      format: [this.data?.booking?.format ?? ""],
      composition: [this.data?.booking?.composition ?? null],
      startMatchmaking: [false],
    });

    // Check if maintenance is already selected
    if (this.data?.booking?.status === "maintenance") {
      this.isMaintenanceMode = true;
      // If it's a complete day maintenance, set duration accordingly
      if (this.data?.booking?.isCompleteDayMaintenance) {
        this.bookingForm.get("duration")?.setValue("completeDay");
      }
    }

    // Manual Player Form
    this.manualPlayerForm = this._formBuilder.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      email: ["", [Validators.required, Validators.email]],
      phone: ["", [Validators.required]],
    });

    if (this.data?.booking?.venueId) {
      this.venueService.getCourtsByVenue(this.data.booking.venueId).subscribe({
        next: (res) => {
          this.courts = res.results || [];
          this.bookingForm.patchValue({ courtId: this.data?.booking?.courtId });
          const selectedCourt = this.courts.find(
            (c) => c._id === this.data?.booking?.courtId,
          );
          if (selectedCourt) {
            this.selectedCourt = selectedCourt;
            this.bookingForm.patchValue({ sports: selectedCourt.sports });
            // Load sports data for the selected court
            this.loadSportsData(selectedCourt.sports);
            // Load court operating hours
            this.loadCourtOperatingHours(selectedCourt);
            this.calculateTotalPrice();
            this.loadCourtAvailability(Boolean(this.savedBookingId));
          }
        },
        error: (err) => console.error("Failed to load courts", err),
      });
    }

    this.bookingForm
      .get("start")
      ?.valueChanges.subscribe(() => this.calculateTotalPrice());
    this.bookingForm.get("duration")?.valueChanges.subscribe(() => {
      this.calculateTotalPrice();
      this.handleDurationChange();
    });

    // Setup autocomplete search
    this.setupPlayerSearch();

    // Load accepted and pending players from existing booking
    this.loadInvitationPlayers();
  }

  get savedBookingId(): string {
    return this.data?.booking?._id || this.data?.booking?.id || "";
  }

  get isAdmin(): boolean {
    return this.authService.getUser()?.role === "admin";
  }

  get excludedPlayerIds(): string[] {
    return [
      ...this.acceptedPlayers,
      ...this.pendingPlayers,
      ...this.selectedPlayers.map((player) => player._id),
    ];
  }

  get displayedParticipants(): any[] {
    return buildBookingParticipantDisplay(
      this.data?.booking,
      this.selectedPlayers,
    );
  }

  selectOrganizer(playerId: string): void {
    if (!this.isPlayerSelected(playerId)) return;
    this.organizerPlayerId =
      this.organizerPlayerId === playerId ? null : playerId;
    this.cdr.markForCheck();
  }

  /**
   * Load court operating hours from court data
   */
  private loadCourtOperatingHours(court: any): void {
    const venue = this.data?.venues?.find(
      (v) => v._id === this.bookingForm.get("venueId")?.value,
    );
    if (!venue?.timing) {
      this.courtOperatingHours = null;
      return;
    }

    let timing = venue.timing;

    if (typeof timing === "string") {
      try {
        timing = JSON.parse(timing);
      } catch {
        timing = [];
      }
    }

    const selectedDate = new Date(this.bookingForm.get("start")?.value);
    const dayName = selectedDate.toLocaleDateString("en-US", {
      weekday: "long",
    });

    const dayTiming = Array.isArray(timing)
      ? timing.find((t) => t.name === dayName && t.checked)
      : timing?.[dayName];

    if (!dayTiming?.startTime || !dayTiming?.endTime) {
      this.courtOperatingHours = null;
      return;
    }

    this.courtOperatingHours = {
      startTime: dayTiming.startTime,
      endTime: dayTiming.endTime,
      dayOfWeek: dayName.toLowerCase(),
    };
  }
  /**
   * Parse time string to hours and minutes
   */
  private parseTime(timeStr: string): { hours: number; minutes: number } {
    if (!timeStr) return { hours: 0, minutes: 0 };

    // Handle 24:00 as 00:00 next day
    if (timeStr === "24:00") {
      return { hours: 0, minutes: 0 }; // Will be handled as next day
    }

    const [hours, minutes] = timeStr.split(":").map(Number);
    return { hours: hours || 0, minutes: minutes || 0 };
  }

  /**
   * Handle duration change for complete day maintenance
   */
  private handleDurationChange(): void {
    if (!this.isMaintenanceSelected() || !this.isCompleteDaySelected()) return;

    const selectedDate = new Date(this.bookingForm.get("start")?.value);
    if (isNaN(selectedDate.getTime())) return;

    // ✅ MUST use real venue timing (same as calendar)
    const venue = this.data?.venues?.find(
      (v) => v._id === this.bookingForm.get("venueId")?.value,
    );

    if (!venue?.timing) return;

    let timing = venue.timing;

    // ✅ Handle JSON string timing
    if (typeof timing === "string") {
      try {
        timing = JSON.parse(timing);
      } catch {
        return;
      }
    }

    const dayName = selectedDate.toLocaleDateString("en-US", {
      weekday: "long",
    });

    const dayTiming = Array.isArray(timing)
      ? timing.find((t) => t.name === dayName && t.checked)
      : timing?.[dayName];

    if (!dayTiming?.startTime || !dayTiming?.endTime) return;

    const startParsed = this.parseTime(dayTiming.startTime);
    const endParsed = this.parseTime(dayTiming.endTime);

    // ✅ SET REAL START
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(startParsed.hours, startParsed.minutes, 0, 0);

    // ✅ SET REAL END
    const endOfDay = new Date(selectedDate);

    if (dayTiming.endTime === "24:00") {
      endOfDay.setHours(24, 0, 0, 0);
    } else if (dayTiming.endTime < dayTiming.startTime) {
      endOfDay.setDate(endOfDay.getDate() + 1);
      endOfDay.setHours(endParsed.hours, endParsed.minutes, 0, 0);
    } else {
      endOfDay.setHours(endParsed.hours, endParsed.minutes, 0, 0);
    }

    this.bookingForm.get("start")?.setValue(startOfDay, { emitEvent: false });
    this.bookingForm.get("end")?.setValue(endOfDay, { emitEvent: false });
    this.totalPrice = 0;

    this.cdr.markForCheck();
  }

  /**
   * Check if maintenance status is selected
   */
  isMaintenanceSelected(): boolean {
    return this.bookingForm.get("status")?.value === "maintenance";
  }

  /**
   * Check if complete day duration is selected
   */
  isCompleteDaySelected(): boolean {
    return this.bookingForm.get("duration")?.value === "completeDay";
  }

  get availableDurationOptions(): number[] {
    const selectedStart = this.selectedAvailabilityStart;
    if (!selectedStart) return [];
    const options = getAvailableBookingDurations(
      this.availabilitySlots,
      selectedStart,
    );
    const originalDuration = this.originalBookingDurationMinutes();
    if (
      this.isOriginalBookingStart(selectedStart) &&
      originalDuration != null &&
      !options.includes(originalDuration)
    ) {
      return [...options, originalDuration].sort((left, right) => left - right);
    }
    return options;
  }

  isAvailabilitySlotSelectable(slot: BookingAvailabilitySlot): boolean {
    return slot.available || this.isOriginalBookingStart(this.slotKey(slot));
  }

  slotKey(slot: BookingAvailabilitySlot): string {
    return bookingAvailabilitySlotKey(slot);
  }

  formatAvailabilitySlot(slot: BookingAvailabilitySlot): string {
    const [hours, minutes] = slot.start.split(":").map(Number);
    const date = new Date(2000, 0, 1, hours, minutes);
    const label = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return slot.dayOffset > 0 ? `${label} (next day)` : label;
  }

  selectAvailabilitySlot(startKey: string): void {
    const slot = this.availabilitySlots.find(
      (candidate) =>
        this.slotKey(candidate) === startKey &&
        this.isAvailabilitySlotSelectable(candidate),
    );
    if (!slot) return;

    if (this.isOriginalBookingStart(startKey)) {
      const originalStart = new Date(this.data?.booking?.start);
      const originalEnd = new Date(this.data?.booking?.end);
      const originalDuration = this.originalBookingDurationMinutes();
      if (originalDuration == null) return;
      this.selectedAvailabilityStart = startKey;
      this.bookingForm.patchValue(
        {
          start: originalStart,
          end: originalEnd,
          duration: String(originalDuration),
        },
        { emitEvent: false },
      );
      this.calculateTotalPrice();
      this.cdr.markForCheck();
      return;
    }

    const selectedDate = new Date(this.bookingForm.get("start")?.value);
    const range = applyBookingSlot(
      selectedDate,
      startKey,
      30,
      this.availabilitySlots,
    );
    if (!range) return;

    this.selectedAvailabilityStart = startKey;
    this.bookingForm.patchValue(
      { start: range.start, end: range.end, duration: null },
      { emitEvent: false },
    );
    this.totalPrice = 0;
    this.effectivePricePerHour = 0;
    this.cdr.markForCheck();
  }

  /**
   * Handle status change
   */
  onStatusChange(event: MatSelectChange): void {
    const status = event.value;
    this.isMaintenanceMode = status === "maintenance";

    if (status === "maintenance") {
      this.availabilityRequestId += 1;
      this.isLoadingAvailability = false;
      this.availabilityError = null;
      this.availabilitySlots = [];
      this.selectedAvailabilityStart = null;
      // Clear any selected players for maintenance
      this.selectedPlayers = [];
      this.playerSearchControl.setValue("");
      this.filteredPlayers = [];

      // Reset manual player form
      this.manualPlayerForm.reset();

      // Set price to 0
      this.totalPrice = 0;

      // If duration is not already set to complete day, set it to default 30 mins
      if (this.bookingForm.get("duration")?.value !== "completeDay") {
        this.bookingForm.get("duration")?.setValue("30");
      }

      // Load court operating hours if court is selected
      if (this.selectedCourt) {
        this.loadCourtOperatingHours(this.selectedCourt);
      }
    } else {
      // Recalculate price if not maintenance
      this.loadCourtAvailability(Boolean(this.savedBookingId));
      this.calculateTotalPrice();
    }

    this.cdr.markForCheck();
  }

  /**
   * Load sports data including logo and color
   */
  private loadSportsData(sportKey: string): void {
    this.bookingService.getSports(sportKey).subscribe({
      next: (sportsResponse) => {
        console.log("Sports API Response:", sportsResponse);
        this.sportsData = sportsResponse;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error("Failed to load sports data", error);
        this.sportsData = {
          logo: "assets/images/default-sport.png",
          color: "#666666",
        };
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Get sports logo URL
   */
  getSportsLogo(): string {
    return this.sportsData?.logo || "assets/images/default-sport.png";
  }

  /**
   * Get sports background color
   */
  getSportsColor(): string {
    return this.sportsData?.color || "#666666";
  }

  /**
   * Setup player search autocomplete functionality
   */
  private setupPlayerSearch(): void {
    this.playerSearchControl.valueChanges.subscribe((searchTerm) => {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      if (searchTerm && searchTerm.trim().length >= 2) {
        // Debounce search to avoid too many API calls
        this.searchTimeout = setTimeout(() => {
          this.searchPlayersByEmailOrPhone(searchTerm.trim());
        }, 300);
      } else {
        this.filteredPlayers = [];
      }
    });
  }
  /**
   * Search players by email or phone number with debouncing
   */
  private searchPlayersByEmailOrPhone(searchTerm: string): void {
    // Clear any existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Debounce for 500ms (half second)
    this.searchTimeout = setTimeout(() => {
      this.isLoadingPlayers = true;
      this.filteredPlayers = [];

      this.bookingService.searchByContact(searchTerm).subscribe({
        next: (response: any) => {
          if (response.success && response.exists && response.player) {
            // Single player found
            const player = response.player;

            // Check if player is already accepted, pending, or selected
            if (
              !this.acceptedPlayers.includes(player._id) &&
              !this.pendingPlayers.includes(player._id) &&
              !this.selectedPlayers.find((p) => p._id === player._id)
            ) {
              this.filteredPlayers = [player];
            } else {
              // Player already in list, don't add them
              this.filteredPlayers = [];
            }
          } else {
            // No player found or error
            this.filteredPlayers = [];
          }

          this.isLoadingPlayers = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error("Failed to search player", error);
          this.filteredPlayers = [];
          this.isLoadingPlayers = false;
          this.cdr.markForCheck();
        },
      });
    }, 250);
  }
  /**
   * Handle player selection from autocomplete
   */
  onPlayerSelected(event: MatAutocompleteSelectedEvent): void {
    const player = event.option.value;
    if (player && player._id) {
      this.addPlayer(player);
      // Clear search input after selection
      this.playerSearchControl.setValue("");
      this.filteredPlayers = [];
    }
  }

  /**
   * Clear player search
   */
  clearPlayerSearch(): void {
    this.playerSearchControl.setValue("");
    this.filteredPlayers = [];
  }

  /**
   * Create a new player manually and add to selected players
   */
  createPlayerManually(): void {
    if (this.manualPlayerForm.invalid) {
      this.manualPlayerForm.markAllAsTouched();
      return;
    }

    this.isCreatingPlayer = true;
    const playerData = this.manualPlayerForm.value;

    this.bookingService.createPlayer(playerData).subscribe({
      next: (response) => {
        const newPlayer = response;
        this.isCreatingPlayer = false;

        // Add the newly created player to selected players
        this.selectedPlayers.push(newPlayer);

        // Reset the manual form
        this.manualPlayerForm.reset();
        this.showExistingPlayers = true;
        this.showNewPlayerForm = false;
        this.cdr.detectChanges();

        this.snackBar.open(
          `${newPlayer.name} created and added to participants`,
          "Close",
          {
            duration: 3000,
            verticalPosition: "top",
          },
        );
      },
      error: (error) => {
        this.isCreatingPlayer = false;
        let errorMessage = "Failed to create player";
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.error?.error?.message) {
          errorMessage = error.error.error.message;
        }
        this.snackBar.open(errorMessage, "Close", {
          duration: 5000,
          verticalPosition: "top",
        });
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Load players with accepted and pending status from existing booking
   */
  private loadInvitationPlayers(): void {
    this.acceptedPlayers = [];
    this.pendingPlayers = [];

    if (
      this.data?.booking?.invitations &&
      Array.isArray(this.data.booking.invitations)
    ) {
      this.acceptedPlayers = this.data.booking.invitations
        .filter((invitation: any) => invitation.status === "accepted")
        .map((invitation: any) => playerIdentity(invitation.invitedUser));

      this.pendingPlayers = this.data.booking.invitations
        .filter((invitation: any) => invitation.status === "pending")
        .map((invitation: any) => playerIdentity(invitation.invitedUser));
    }
  }

  /**
   * Get players who have accepted the invitation
   */
  getAcceptedPlayers(): any[] {
    if (!this.data?.booking?.invitations) return [];

    const acceptedInvitations = this.data.booking.invitations.filter(
      (invitation: any) => invitation.status === "accepted",
    );

    // Map invitation.invitedUser to player objects from allPlayers
    return acceptedInvitations
      .map((invitation) => {
        const player = this.allPlayers.find(
          (p) => p._id === invitation.invitedUser,
        );
        return (
          player || {
            _id: invitation.invitedUser,
            name: "Unknown Player",
            email: "",
            avatar: null,
          }
        );
      })
      .filter((player) => player);
  }

  /**
   * Get players who have pending invitations
   */
  getPendingPlayers(): any[] {
    if (!this.data?.booking?.invitations) return [];

    const pendingInvitations = this.data.booking.invitations.filter(
      (invitation: any) => invitation.status === "pending",
    );

    return pendingInvitations
      .map((invitation) => {
        const player = this.allPlayers.find(
          (p) => p._id === invitation.invitedUser,
        );
        return (
          player || {
            _id: invitation.invitedUser,
            name: "Unknown Player",
            email: "",
            avatar: null,
          }
        );
      })
      .filter((player) => player);
  }

  addPlayer(player: any): void {
    if (!player || !player._id) return;

    // Don't allow adding players in maintenance mode
    if (this.isMaintenanceSelected()) {
      this.snackBar.open(
        "Cannot add participants in maintenance mode",
        "Close",
        {
          duration: 3000,
          verticalPosition: "top",
        },
      );
      return;
    }

    if (this.acceptedPlayers.includes(player._id)) {
      this.snackBar.open(
        `${player.name} has already accepted the invitation and cannot be added`,
        "Close",
        {
          duration: 3000,
          verticalPosition: "top",
        },
      );
      return;
    }

    if (!this.selectedPlayers.find((p) => p._id === player._id)) {
      this.selectedPlayers.push(player);
      this.snackBar.open(`${player.name} added to participants`, "Close", {
        duration: 2000,
        verticalPosition: "top",
      });
      this.cdr.markForCheck();
    } else {
      this.snackBar.open(`${player.name} is already added`, "Close", {
        duration: 3000,
        verticalPosition: "top",
      });
    }
  }

  removePlayer(playerId: string): void {
    // Don't allow removing players in maintenance mode (but there shouldn't be any)
    if (this.isMaintenanceSelected()) {
      return;
    }

    const player = this.selectedPlayers.find((p) => p._id === playerId);
    this.selectedPlayers = this.selectedPlayers.filter(
      (player) => player._id !== playerId,
    );
    if (this.organizerPlayerId === playerId) {
      this.organizerPlayerId = null;
    }

    if (player) {
      this.snackBar.open(`${player.name} removed from participants`, "Close", {
        duration: 2000,
        verticalPosition: "top",
      });
    }

    this.cdr.markForCheck();
  }

  isPlayerSelected(playerId: string): boolean {
    return this.selectedPlayers.some((player) => player._id === playerId);
  }

  getStartTime(): string {
    if (this.isMaintenanceSelected() && this.isCompleteDaySelected()) {
      // Get the operating hours for display
      const startDate = new Date(this.bookingForm.get("start")?.value);
      if (!isNaN(startDate.getTime())) {
        const hours = startDate.getHours().toString().padStart(2, "0");
        const minutes = startDate.getMinutes().toString().padStart(2, "0");
        return `${hours}:${minutes}`;
      }
      return "Start Time";
    }

    const startValue = this.bookingForm.get("start")?.value;
    if (!startValue) return "Start Time";

    try {
      const date = new Date(startValue);
      if (isNaN(date.getTime())) return "Start Time";
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "Start Time";
    }
  }

  getEndTime(): string {
    if (this.isMaintenanceSelected() && this.isCompleteDaySelected()) {
      // Get the operating hours for display
      const endDate = new Date(this.bookingForm.get("end")?.value);
      if (!isNaN(endDate.getTime())) {
        const startDate = new Date(this.bookingForm.get("start")?.value);
        const isNextDay =
          endDate.getFullYear() !== startDate.getFullYear() ||
          endDate.getMonth() !== startDate.getMonth() ||
          endDate.getDate() !== startDate.getDate();
        if (
          isNextDay &&
          endDate.getHours() === 0 &&
          endDate.getMinutes() === 0
        ) {
          return "24:00";
        }
        const hours = endDate.getHours().toString().padStart(2, "0");
        const minutes = endDate.getMinutes().toString().padStart(2, "0");
        return `${hours}:${minutes}${isNextDay ? " (next day)" : ""}`;
      }
      return "End Time";
    }

    const endValue = this.bookingForm.get("end")?.value;
    if (!endValue) return "End Time";

    try {
      const date = new Date(endValue);
      if (isNaN(date.getTime())) return "End Time";
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "End Time";
    }
  }

  getFormattedDate(): string {
    const startValue = this.bookingForm.get("start")?.value;
    if (!startValue) return "Select Date";

    try {
      const date = new Date(startValue);
      if (isNaN(date.getTime())) return "Select Date";
      return formatProductDate(date);
    } catch {
      return "Select Date";
    }
  }

  private calculateTotalPrice() {
    const start = this.bookingForm.get("start")?.value;
    const durationValue = this.bookingForm.get("duration")?.value;

    if (!start || !durationValue) {
      this.totalPrice = 0;
      this.effectivePricePerHour = 0;
      return;
    }

    const startDate = new Date(start);
    if (isNaN(startDate.getTime())) return;

    // ✅ HANDLE COMPLETE DAY MAINTENANCE
    if (this.isMaintenanceSelected() && durationValue === "completeDay") {
      this.totalPrice = 0;
      this.effectivePricePerHour = 0;
      this.cdr.markForCheck();
      return;
    }

    const durationMins = parseInt(durationValue);

    // ✅ HANDLE 30 / 60 / 90 MAINTENANCE PROPERLY
    const end = new Date(startDate.getTime() + durationMins * 60000);
    this.bookingForm.get("end")?.setValue(end, { emitEvent: false });

    if (this.isMaintenanceSelected()) {
      this.totalPrice = 0;
      this.effectivePricePerHour = 0;
    } else if (this.selectedCourt) {
      const preview = previewCourtPrice(
        this.selectedCourt,
        startDate,
        durationMins,
      );
      this.totalPrice = preview.total;
      this.effectivePricePerHour = preview.pricePerHour;
    } else {
      this.totalPrice = 0;
      this.effectivePricePerHour = 0;
    }

    this.cdr.markForCheck();
  }

  fetchVenues(): void {
    this.globalError = null;
    this.venueService.getVenues().subscribe(
      (venues: any) => {
        this.venues = venues;
        this.cdr.markForCheck();
      },
      (error) => {
        this.showError(error?.error, "Failed to load venues.");
      },
    );
  }

  onDateChange(event: any): void {
    const selectedDate = event.value;
    if (selectedDate) {
      const currentStart = new Date(
        this.bookingForm.get("start")?.value || new Date(),
      );
      const newDate = new Date(selectedDate);

      newDate.setHours(currentStart.getHours());
      newDate.setMinutes(currentStart.getMinutes());
      newDate.setSeconds(0);
      newDate.setMilliseconds(0);

      this.bookingForm.get("start")?.setValue(newDate);

      // Update operating hours if court is selected
      if (this.selectedCourt) {
        this.loadCourtOperatingHours(this.selectedCourt);
        this.loadCourtAvailability(false);
      }

      this.calculateTotalPrice();
    }
  }

  onTimeChange(event: any): void {
    const selectedTime = event.value;
    if (selectedTime) {
      const currentStart = new Date(
        this.bookingForm.get("start")?.value || new Date(),
      );
      const [hours, minutes] = selectedTime.split(":");

      currentStart.setHours(parseInt(hours));
      currentStart.setMinutes(parseInt(minutes));
      currentStart.setSeconds(0);
      currentStart.setMilliseconds(0);

      this.bookingForm.get("start")?.setValue(currentStart);
      this.calculateTotalPrice();
    }
  }

  durationChange($event: any): void {
    if (!this.isMaintenanceSelected() && this.selectedAvailabilityStart) {
      const duration = Number(this.bookingForm.get("duration")?.value);
      if (!this.availableDurationOptions.includes(duration)) return;
      const selectedDate = new Date(this.bookingForm.get("start")?.value);
      const range = applyBookingSlot(
        selectedDate,
        this.selectedAvailabilityStart,
        duration,
        this.availabilitySlots,
      );
      if (!range) return;
      this.bookingForm.patchValue(
        { start: range.start, end: range.end },
        { emitEvent: false },
      );
    }
    this.calculateTotalPrice();
  }

  fetchCourts($event?: any): void {
    this.globalError = null;
    const venueId = this.bookingForm.get("venueId").value;

    this.venueService.getCourtsByVenue(venueId).subscribe(
      (response: any) => {
        this.courts = response.results || response;
        this.selectedCourt = null;
        this.availabilityRequestId += 1;
        this.isLoadingAvailability = false;
        this.availabilityError = null;
        this.availabilitySlots = [];
        this.selectedAvailabilityStart = null;
        this.bookingForm.get("courtId").setValue("");

        // Clear player search when venue changes
        this.clearPlayerSearch();

        this.cdr.markForCheck();
      },
      (error) => {
        this.showError(error?.error, "Failed to load courts.");
      },
    );
  }

  selectCourt(event: any) {
    this.selectedCourt = this.courts.find((v) => v._id === event.value);
    this.bookingForm.patchValue({
      sports: this.selectedCourt ? this.selectedCourt.sports : "soccer",
    });
    // Load sports data when court is selected
    if (this.selectedCourt?.sports) {
      this.loadSportsData(this.selectedCourt.sports);
    }
    // Load court operating hours
    if (this.selectedCourt) {
      this.loadCourtOperatingHours(this.selectedCourt);
      this.loadCourtAvailability(false);
    }
    this.calculateTotalPrice();
    this.cdr.markForCheck();
  }

  private loadCourtAvailability(preserveSelection: boolean): void {
    const requestId = ++this.availabilityRequestId;
    if (this.isMaintenanceSelected()) {
      this.isLoadingAvailability = false;
      return;
    }
    const venueId = String(this.bookingForm.get("venueId")?.value ?? "").trim();
    const courtId = String(this.bookingForm.get("courtId")?.value ?? "").trim();
    const selectedDate = new Date(this.bookingForm.get("start")?.value);
    const date = formatAvailabilityDate(selectedDate);
    if (!venueId || !courtId || !date) {
      this.availabilitySlots = [];
      this.selectedAvailabilityStart = null;
      this.isLoadingAvailability = false;
      return;
    }

    if (!preserveSelection) {
      this.selectedAvailabilityStart = null;
      this.bookingForm.patchValue({ duration: null }, { emitEvent: false });
    }
    this.isLoadingAvailability = true;
    this.availabilityError = null;
    this.bookingService.getCourtAvailability(venueId, courtId, date).subscribe({
      next: (response) => {
        if (requestId !== this.availabilityRequestId) return;
        this.availabilitySlots = normalizeBookingAvailability(response);
        if (preserveSelection) {
          const start = new Date(this.bookingForm.get("start")?.value);
          this.selectedAvailabilityStart = start.toISOString();
          if (
            this.selectedAvailabilityStart &&
            !this.availabilitySlots.some(
              (slot) => this.slotKey(slot) === this.selectedAvailabilityStart,
            )
          ) {
            const originalEnd = new Date(this.data?.booking?.end);
            this.availabilitySlots = [
              ...this.availabilitySlots,
              {
                available: false,
                dayOffset: 0,
                start:
                  formatAvailabilityTime(start) ??
                  this.selectedAvailabilityStart,
                startUtc: start.toISOString(),
                end:
                  formatAvailabilityTime(originalEnd) ??
                  this.selectedAvailabilityStart,
                endUtc: originalEnd.toISOString(),
              },
            ].sort((left, right) =>
              this.slotKey(left).localeCompare(this.slotKey(right)),
            );
          }
        }
        this.isLoadingAvailability = false;
        this.cdr.markForCheck();
      },
      error: () => {
        if (requestId !== this.availabilityRequestId) return;
        this.availabilitySlots = [];
        this.availabilityError = "Unable to load available time slots.";
        this.isLoadingAvailability = false;
        this.cdr.markForCheck();
      },
    });
  }

  private originalBookingDurationMinutes(): number | null {
    const start = new Date(this.data?.booking?.start);
    const end = new Date(this.data?.booking?.end);
    const duration = Math.round((end.getTime() - start.getTime()) / 60_000);
    return Number.isFinite(duration) && duration > 0 ? duration : null;
  }

  private isOriginalBookingStart(startKey: string): boolean {
    if (!this.savedBookingId) return false;
    const original = new Date(this.data?.booking?.start);
    const selected = new Date(this.bookingForm.get("start")?.value);
    return (
      Number.isFinite(original.getTime()) &&
      Number.isFinite(selected.getTime()) &&
      String(this.data?.booking?.courtId) ===
        String(this.bookingForm.get("courtId")?.value) &&
      (startKey === original.toISOString() ||
        (formatAvailabilityDate(original) ===
          formatAvailabilityDate(selected) &&
          formatAvailabilityTime(original) === startKey))
    );
  }

  private hasValidFixedSlotSelection(): boolean {
    if (this.isMaintenanceSelected()) return true;
    const duration = Number(this.bookingForm.get("duration")?.value);
    return (
      this.selectedAvailabilityStart != null &&
      this.availableDurationOptions.includes(duration)
    );
  }

  /**
   * Check booking status and send invitations only to users not already accepted
   */
  sendInvitations(bookingId: string): void {
    // Don't send invitations for maintenance bookings
    if (this.isMaintenanceSelected()) {
      return;
    }

    if (!this.selectedPlayers.length) {
      this.snackBar.open("No players selected to invite", "Close", {
        duration: 3000,
      });
      return;
    }

    this.isInviting = true;

    this.bookingService.getBookingById(bookingId).subscribe({
      next: (userBooking) => {
        if (userBooking?.status === "pending") {
          this.isInviting = false;
          this.snackBar.open(
            "Cannot send invitations for pending bookings",
            "Close",
            {
              duration: 5000,
              verticalPosition: "top",
            },
          );
          return;
        }
        const alreadyAcceptedUsers: string[] = [];
        if (
          userBooking?.invitations &&
          Array.isArray(userBooking.invitations)
        ) {
          userBooking.invitations.forEach((invitation: any) => {
            if (invitation.status === "accepted" && invitation.invitedUser) {
              alreadyAcceptedUsers.push(invitation.invitedUser);
            }
          });
        }
        const alreadyInvitedUsers: string[] = [];
        if (
          userBooking?.invitations &&
          Array.isArray(userBooking.invitations)
        ) {
          userBooking.invitations.forEach((invitation: any) => {
            if (
              ["accepted", "pending"].includes(invitation.status) &&
              invitation.invitedUser
            ) {
              alreadyInvitedUsers.push(invitation.invitedUser);
            }
          });
        }
        const playersToInvite = this.selectedPlayers.filter(
          (player) => !alreadyInvitedUsers.includes(player._id),
        );
        if (playersToInvite.length === 0) {
          this.isInviting = false;
          this.snackBar.open(
            "All selected players have already accepted the invitation",
            "Close",
            {
              duration: 5000,
              verticalPosition: "top",
            },
          );
          return;
        }
        const playerIds = playersToInvite.map((player) => player._id);

        const organizerPlayerId =
          this.organizerPlayerId && playerIds.includes(this.organizerPlayerId)
            ? this.organizerPlayerId
            : undefined;
        this.bookingService
          .inviteToBooking(bookingId, playerIds, organizerPlayerId)
          .subscribe({
            next: (response) => {
              this.isInviting = false;
              this.snackBar.open(
                `Invitations sent to ${playersToInvite.length} player(s)`,
                "Close",
                {
                  duration: 3000,
                  verticalPosition: "top",
                },
              );
              this.cdr.markForCheck();
            },
            error: (error) => {
              this.isInviting = false;
              let errorMessage = "Failed to send invitations";
              if (error.error?.message) {
                errorMessage = error.error.message;
              } else if (error.error?.error?.message) {
                errorMessage = error.error.error.message;
              }
              this.snackBar.open(errorMessage, "Close", {
                duration: 5000,
                verticalPosition: "top",
              });
              this.cdr.markForCheck();
            },
          });
      },
      error: (error) => {
        this.isInviting = false;
        let errorMessage = "Failed to check booking details";
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.error?.error?.message) {
          errorMessage = error.error.error.message;
        }
        this.snackBar.open(errorMessage, "Close", {
          duration: 5000,
          verticalPosition: "top",
        });
        this.cdr.markForCheck();
      },
    });
  }

  onSubmit(): void {
    this.saveBooking(
      !this.savedBookingId &&
        this.bookingForm.get("startMatchmaking")?.value === true,
    );
  }

  private saveBooking(keepOpenForMatchmaking: boolean): void {
    this.globalError = null;
    if (!this.hasValidFixedSlotSelection()) {
      this.showError(
        "Select an available time slot and duration before saving.",
        "",
      );
      return;
    }
    if (this.bookingForm.invalid) {
      this.showError("Please fix the highlighted fields and try again.", "");
      this.scrollToFirstError();
      return;
    }

    if (this.bookingForm.valid) {
      const startDate = formatVenueWallClockInput(
        this.bookingForm.get("start")?.value,
      );
      const endDate = formatVenueWallClockInput(
        this.bookingForm.get("end")?.value,
      );
      if (!startDate || !endDate) {
        this.showError(
          "Invalid date format. Please check your date and time selection.",
          "",
        );
        return;
      }

      // Create booking WITHOUT playersList - players will be added via invitations
      const selectedBooking: any = {
        id: this.bookingForm.get("id")?.value,
        venueId: this.bookingForm.get("venueId")?.value,
        courtId: this.bookingForm.get("courtId")?.value,
        sport: this.selectedCourt?.sports || "soccer",
        title:
          (this.selectedCourt ? this.selectedCourt.name : "Court") +
          " - " +
          (this.selectedCourt?.sports || "soccer"),
        start: startDate,
        end: endDate,
        currency: "AED",
        pricePerHour: this.effectivePricePerHour,
        tax: 0,
        discount: 0,
        total: this.totalPrice,
        refund: "",
        status: this.bookingForm.get("status")?.value,
        canceledAt:
          this.bookingForm.get("status")?.value === "canceled"
            ? new Date().toISOString()
            : null,
        cancellationReason: "",
        notes: this.bookingForm.get("notes")?.value,
        bookingType: this.isMaintenanceSelected()
          ? "private"
          : this.bookingForm.get("bookingType")?.value,
        ...(this.bookingForm.get("bookingType")?.value === "public" &&
        !this.isMaintenanceSelected()
          ? {
              slots: Number(this.bookingForm.get("slots")?.value),
              format: String(
                this.bookingForm.get("format")?.value ?? "",
              ).trim(),
              composition: this.bookingForm.get("composition")?.value,
            }
          : {}),
      };

      // Add maintenance specific properties
      if (this.isMaintenanceSelected()) {
        selectedBooking.isCompleteDayMaintenance = this.isCompleteDaySelected();
        selectedBooking.isMaintenance = true;
        // Force total to 0 for maintenance
        selectedBooking.total = 0;
        selectedBooking.pricePerHour = 0;

        // Add operating hours info
        if (this.courtOperatingHours) {
          selectedBooking.operatingHours = this.courtOperatingHours;
        }
      }

      this.isLoading = true;
      this.bookingService.createOrUpdateBooking(selectedBooking).subscribe({
        next: (response) => {
          const bookingId = response._id || response.id;

          // Only send invitations if not maintenance
          if (
            this.selectedPlayers.length > 0 &&
            bookingId &&
            !this.isMaintenanceSelected()
          ) {
            this.sendInvitations(bookingId);
          }

          this.snackBar.open("Booking saved successfully", "Close", {
            duration: 3000,
          });
          this.isLoading = false;
          if (keepOpenForMatchmaking) {
            this.data.booking = response;
            this.bookingForm.get("id")?.setValue(bookingId, {
              emitEvent: false,
            });
            this.snackBar.open(
              "Booking saved. Review its eligibility, then start Match Making.",
              "Close",
              { duration: 5000 },
            );
            this.cdr.markForCheck();
          } else {
            this.dialogRef.close(response);
          }
        },
        error: (error) => {
          let errorMessage = "Error saving booking";
          if (error.error?.error?.message) {
            errorMessage = error.error.error.message;
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.error?.error) {
            errorMessage =
              typeof error.error.error === "string"
                ? error.error.error
                : JSON.stringify(error.error.error);
          } else if (error.status === 400) {
            errorMessage =
              "Invalid data sent to server. Please check all required fields.";
          }

          this.showError(errorMessage, "Error saving booking");
          this.isLoading = false;
        },
      });
    }
  }

  private scrollToFirstError(): void {
    this.bookingForm.markAllAsTouched();
    this.cdr.detectChanges();

    const el: HTMLElement | null = document.querySelector(
      ".ng-invalid[formcontrolname], .ng-invalid[ng-reflect-name]",
    );
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      (el as any).focus?.();
    }
  }

  private showError(err: any, fallback: string): void {
    const msg = this.getErrorMessage(err) || fallback;
    this.globalError = msg;
    this.snackBar.open(msg, "Dismiss", {
      duration: 6000,
      panelClass: ["snack-error"],
      horizontalPosition: "right",
      verticalPosition: "top",
    });
    console.error("[Booking] Error:", err);
    this.cdr.markForCheck();
  }

  private getErrorMessage(err: any): string {
    if (!err) return "Unexpected error";
    const http = err as HttpErrorResponse;
    const payload = http?.error;
    if (payload) {
      if (typeof payload === "string") return payload;
      if (payload.message) return payload.message;
      if (Array.isArray(payload.errors) && payload.errors.length) {
        const msgs = payload.errors
          .map((e: any) => e?.message || `${e?.field ?? "Field"} is invalid`)
          .join("\n");
        return msgs;
      }
    }
    if (http?.message) return http.message;
    if (typeof err === "string") return err;
    return "Something went wrong. Please try again.";
  }

  dismissGlobalError() {
    this.globalError = null;
  }
}
